import type { FragmentOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import { Table } from "@opensea/ui-kit"
import { useIsFlowActive } from "../../flow/hooks/useIsFlowActive"
import { AcceptOffersSummary } from "../AcceptOffersSummary"
import { ReceiptDisplay } from "../Receipt"
import type { PricingLookup } from "./helpers"
import { QuoteReceiptFragment } from "./QuoteReceipt.fragment"
import { QuoteReceiptProvider } from "./QuoteReceiptProvider"
import { QuoteReceiptRow } from "./QuoteReceiptRow"
import { QuoteReceiptTotal } from "./QuoteReceiptTotal"

type Props = {
  initialPrices?: PricingLookup
  quote: FragmentOf<typeof QuoteReceiptFragment>
  applyCreatorFee?: boolean
  onCreatorFeeChange?: (enabled: boolean) => void
  hidePricing?: boolean
}

export const QuoteReceipt = ({
  initialPrices,
  quote: quoteKey,
  applyCreatorFee,
  onCreatorFeeChange,
  hidePricing,
}: Props) => {
  const quote = readFragment(QuoteReceiptFragment, quoteKey)
  const isAcceptOfferFlow = useIsFlowActive("accept-offer")

  const receipt = (
    <Table
      itemKey={quote => `${quote?.item.contractAddress}:${quote?.item.tokenId}`}
      items={quote.itemQuotes}
      renderRow={QuoteReceiptRow}
      size="xl"
    />
  )

  const receiptTotal = hidePricing ? null : isAcceptOfferFlow ? (
    <AcceptOffersSummary
      applyCreatorFee={applyCreatorFee}
      onCreatorFeeChange={onCreatorFeeChange}
      quote={quote}
    />
  ) : (
    <Table
      itemKey={(_, index) => index}
      items={[quote.totalPrice]}
      renderRow={QuoteReceiptTotal}
    />
  )

  return (
    <QuoteReceiptProvider
      hidePricing={hidePricing}
      initialPrices={initialPrices}
    >
      <ReceiptDisplay receipt={receipt} receiptTotal={receiptTotal} />
    </QuoteReceiptProvider>
  )
}
